import axios from 'axios';
import audiobufferToWav from 'audiobuffer-to-wav';
import { calculateBPM } from './beat-matching';
import tuner from './tuner';
import audio from 'app-core/audio';
import { guess } from 'web-audio-beat-detector';
import { formatNumber } from 'app-core/Data';

const ctx = new AudioContext();

function StereoAnalyserNode(audioContext, opts) {
	opts = opts || {};
	
	var splitter = audioContext.createChannelSplitter(2);
	var analyserL = audioContext.createAnalyser();
	var analyserR = audioContext.createAnalyser();
	var merger = audioContext.createChannelMerger(2);
	var fftSize = opts.fftSize;
	var minDecibels = opts.minDecibels;
	var maxDecibels = opts.maxDecibels;
	var smoothingTimeConstant = opts.smoothingTimeConstant;
	var hasError = false;
	
	splitter.channelCount = 2;
	splitter.channelCountMode = "explicit";
	
	analyserL.channelCount = 1;
	analyserL.channelCountMode = "explicit";
	
	analyserR.channelCount = 1;
	analyserR.channelCountMode = "explicit";
	
	merger.channelCount = 1;
	merger.channelCountMode = "explicit";
	
	splitter.connect(analyserL, 0, 0);
	splitter.connect(analyserR, 1, 0);
	
	if (typeof analyserL.getFloatTimeDomainData !== "function") {
		analyserL.getFloatTimeDomainData = getFloatTimeDomainData;
		analyserR.getFloatTimeDomainData = getFloatTimeDomainData;
	}
	
	if (typeof fftSize === "number") {
		analyserL.fftSize = fftSize;
		analyserR.fftSize = fftSize;
	}
	
	try {
		if (typeof minDecibels === "number") {
		analyserL.minDecibels = minDecibels;
		analyserR.minDecibels = minDecibels;
		}
	} catch (e) {
		hasError = true;
	}
	
	if (typeof maxDecibels === "number") {
		analyserL.maxDecibels = maxDecibels;
		analyserR.maxDecibels = maxDecibels;
	}
	
	if (hasError) {
		analyserL.minDecibels = minDecibels;
		analyserR.minDecibels = minDecibels;
	}
	
	if (typeof smoothingTimeConstant === "number") {
		analyserL.smoothingTimeConstant = smoothingTimeConstant;
		analyserR.smoothingTimeConstant = smoothingTimeConstant;
	}
	
	Object.defineProperties(splitter, {
		fftSize: {
			set: function(value) {
				analyserL.fftSize = value;
				analyserR.fftSize = value;
			},
			get: function() {
				return analyserL.fftSize;
			},
			enumerable: true
			},
			frequencyBinCount: {
			get: function() {
				return analyserL.frequencyBinCount;
			},
			enumerable: true
			},
			minDecibels: {
			set: function(value) {
				analyserL.minDecibels = value;
				analyserR.minDecibels = value;
			},
			get: function() {
				return analyserL.minDecibels;
			},
			enumerable: true
			},
			maxDecibels: {
			set: function(value) {
				analyserL.maxDecibels = value;
				analyserR.maxDecibels = value;
			},
			get: function() {
				return analyserL.maxDecibels;
			},
			enumerable: true
			},
			smoothingTimeConstant: {
			set: function(value) {
				analyserL.smoothingTimeConstant = value;
				analyserR.smoothingTimeConstant = value;
			},
			get: function() {
				return analyserL.smoothingTimeConstant;
			},
			enumerable: true
			},
			connect: {
			value: function() {
				analyserL.connect(merger, 0, 0);
				analyserR.connect(merger, 0, 1);
				merger.connect.apply(merger, arguments);
			}
			},
			disconnect: {
			value: function() {
				if (arguments.length !== 0) {
				global.console.warn(
					"StereoAnalyserNode does not support selective disconnection. This operation may not works to analyse fine."
				);
				}
				analyserL.disconnect();
				analyserR.disconnect();
				merger.disconnect.apply(merger, arguments);
			}
			},
			getFloatFrequencyData: {
			value: function(arrayL, arrayR) {
				analyserL.getFloatFrequencyData(arrayL);
				analyserR.getFloatFrequencyData(arrayR);
			}
			},
			getByteFrequencyData: {
			value: function(arrayL, arrayR) {
				analyserL.getByteFrequencyData(arrayL);
				analyserR.getByteFrequencyData(arrayR);
			}
			},
			getFloatTimeDomainData: {
			value: function(arrayL, arrayR) {
				analyserL.getFloatTimeDomainData(arrayL);
				analyserR.getFloatTimeDomainData(arrayR);
			}
			},
			getByteTimeDomainData: {
			value: function(arrayL, arrayR) {
				analyserL.getByteTimeDomainData(arrayL);
				analyserR.getByteTimeDomainData(arrayR);
			}
		},
	});
	
	return splitter;
}
	
function getFloatTimeDomainData(array) {
	var uint8 = new global.Uint8Array(array.length);
	
	this.getByteTimeDomainData(uint8);
	
	for (var i = 0, imax = array.length; i < imax; i++) {
		array[i] = ((uint8 - 128) / 128);
	}
}

function bufferToWave(abuffer, len) {
	return new Blob([new DataView(audiobufferToWav(abuffer))], {
		type: 'audio/wav'
	});
}

async function detectBeat(buffer, context) {
	let beatGuess = await guess(buffer).catch(() => ({ bpm: 0, offset: 0, tempo: 0 }));

	if (beatGuess.bpm) {
		if (__DEBUG__)
			console.debug('Beat detected (custom)', { beatGuess });

		return {
			bpm: beatGuess.bpm,
			beatStart: beatGuess.offset
		}
	}

	const bmBeatResult = await calculateBPM(context, buffer);
	if (__DEBUG__)
		console.debug('Beat detected', { bmBeatResult });
	
	return bmBeatResult;
}

async function getNextBeatPoint(buffer, context, start, searchDuration) {
	searchDuration = searchDuration || 4;
	const bufferPart = await audio.cut(buffer, start, start + searchDuration);
	const beat = await detectBeat(bufferPart, context).catch(() => {});

	if (__DEBUG__)
		console.debug('getNextBeatPoint', { beat, start, searchDuration });

	return beat?.guesses?.filter(f => f.avg > beat?.avgPeak && f.time > 0.2)[0]?.time;
}

function getPeakValue(audioBuffer) {
	let array = audioBuffer.getChannelData(0);
	var gap = audioBuffer.duration * 200;
	var eachBlock = Math.floor(array.length / gap);
	
	let peakPower = 0;
	for(var i = 0; i <= gap; i++){
		var audioBuffKey = Math.floor(eachBlock * i);
		const power = array[audioBuffKey] ** 2 || 0;
		peakPower = Math.max(power, peakPower);
	}
	const peakInstantaneousPowerDecibels = 10 * Math.log10(peakPower);

	return peakInstantaneousPowerDecibels;
}

async function getAudioBuffer(dataUrl, context) {
	var blob = dataUrl instanceof Blob ? dataUrl : (await axios.get(dataUrl, { responseType: 'blob' })).data;
	return new Promise(res => {
		var fr = new FileReader();
		fr.readAsArrayBuffer(blob);
		fr.onloadend = () => {
			(context || ctx).decodeAudioData(fr.result, (buffer) => res(buffer), () => res(null));
		};
		fr.onerror = () => res(null);
	});
}

async function detectSilence(audioBuffer, db, opts) {
	if (__DEBUG__)
		console.debug('detectSilence', { audioBuffer, db, opts });
	if (audioBuffer && !audioBuffer?.getChannelData) {
		audioBuffer = await getAudioBuffer(audioBuffer);
	}
	let array = audioBuffer.getChannelData(0);
	var timeStep = 0.01;
	var getSilenceLength = (currentTime, checkFunc, incValue, dbLevel) => {
		let delta = 0;
		while (checkFunc(currentTime)) {
			delta += Math.abs(incValue);
			var timeData = array.slice(currentTime * audioBuffer.sampleRate, currentTime * audioBuffer.sampleRate + 10);

			var peakPower = 0;
			timeData.map(f => {
				const power = f ** 2 || 0;
				peakPower = Math.max(power, peakPower);
			});
			let peakInstantaneousPowerDecibels = 10 * Math.log10(peakPower);
			if (peakInstantaneousPowerDecibels > dbLevel) {
				return delta;
			}
			currentTime += incValue;
		}
	}

	const start = getSilenceLength(0, (t) => t < audioBuffer.duration, timeStep, opts?.dbStart || db);
	const end = getSilenceLength(audioBuffer.duration, (t) => t > 0, -timeStep, opts?.dbEnd || db);

	if (__DEBUG__)
		console.debug('Get silence', { start, end, audioBuffer });

	return {
		start,
		end
	};
}
function dbToGain(db) {
	return Math.pow(10, (db / 20));
}

function gainToDb(gain) {
	return 20 * Math.log10(gain);
}

function gainToDbText(v, units = 'dB') {
	return (v >= 1 ? '+' : '') + formatNumber(gainToDb(Math.max(0.001, v)), `##0.#${units}`);
}

async function blob2Buffer(blob) {
	const arrayBuffer = await blob.arrayBuffer();
	return new Promise(res => ctx.decodeAudioData(arrayBuffer, (buffer) => res(buffer), () => res(null)));
}

async function resampleBuffer(source, targetSampleRate, opts) {
	if (!source || !targetSampleRate) return;

	const TARGET_SAMPLE_RATE = targetSampleRate;
	
	var offlineCtx = new OfflineAudioContext(
		opts?.mono ? 1 : source.numberOfChannels,
		source.duration * TARGET_SAMPLE_RATE,
		TARGET_SAMPLE_RATE
	);

	// Play it from the beginning.
	var offlineSource = offlineCtx.createBufferSource();
	offlineSource.buffer = source;
	offlineSource.connect(offlineCtx.destination);
	offlineSource.start();
	return offlineCtx.startRendering().then((resampled) => {
		return resampled;
	});
}

async function resampleBlob(blob, targetSampleRate, opts) {
	const source = await blob2Buffer(blob);
	const buffer = await resampleBuffer(source, targetSampleRate, opts);
	if (__DEBUG__) {
		window.source = source;
		window.buffer = buffer;
	}
	return new Blob([new DataView(audiobufferToWav(buffer))], {
		type: 'audio/wav'
	});
}

async function calcExactLength(buffer, beatInfo, { targetLength, trimSilence, bufferInfo, fadeOut }) {
	fadeOut = fadeOut !== void 0 ? fadeOut : 0.5;
	bufferInfo = bufferInfo || getBufferInfo(buffer);

	const fadeOutPoint = -24 + bufferInfo.peakDb;

	let fullPartBPM = beatInfo.bpm;

	let leftStartOffset = 0;
	let rightEndOffset = 0;

	if (trimSilence) {
		const silence = await detectSilence(buffer, trimSilence, { dbEnd: fadeOutPoint });
		leftStartOffset = silence.start || 0;
		rightEndOffset = Math.max(0, silence.end || 0 - fadeOut);
	}

	const targetHalfWidth = targetLength / 2;
	const bpmDuration = 60 / fullPartBPM;
	const start = Math.max(0, beatInfo.beatStart > (targetHalfWidth / 2) ? 0 : (beatInfo.beatStart - leftStartOffset));
	
	const leftWithBeatDuration = leftStartOffset + start + Math.max(bpmDuration, (Math.floor((targetHalfWidth - start) / bpmDuration) /* + 1*/) * bpmDuration) + 0;

	let startOffset = leftWithBeatDuration;
	while (startOffset < (buffer.duration - targetHalfWidth - bpmDuration - rightEndOffset)) {
		startOffset += bpmDuration;
	}
	
	const rightWithBeatDuration = buffer.duration - startOffset - rightEndOffset;

	const result = {
		left: {
			duration: leftWithBeatDuration
		},
		right: {
			duration: rightWithBeatDuration
		}
	}

	if (__DEBUG__)
		console.debug('Calc exact length', { startOffset, bufDuration: buffer.duration, start, leftWithBeatDuration, rightWithBeatDuration, buffer, bpmDuration, beatInfo, targetLength, fadeOutPoint, result, fullPartBPM, leftStartOffset, rightEndOffset, trimSilence });

	result.left.endOffset = Math.min(buffer.duration - 1, buffer.duration - leftWithBeatDuration - leftStartOffset);
	result.left.startOffset = leftStartOffset;
	result.right.startOffset = (buffer.duration - rightWithBeatDuration - rightEndOffset);
	result.right.endOffset = rightEndOffset;

	const actualDuration = (result.left.duration + result.right.duration);
	result.playbackRate = actualDuration / targetLength;

	return result;
}

function getBufferInfo(decodedBuffer) {
	const sampleRate = decodedBuffer.sampleRate;
    const channels = decodedBuffer.numberOfChannels;
    const bufferLength = decodedBuffer.length;

    // Constants
    const WINDOW_MOMENTARY = Math.floor(sampleRate * 0.4); // 400ms (momentary)
    const WINDOW_SHORT_TERM = Math.floor(sampleRate * 3); // 3s (short-term)
    const THRESHOLD_LUFS = -70; // ITU-R BS.1770 window limit

    let momentaryMaxLUFS = -Infinity;
    let shortTermMaxLUFS = -Infinity;
    let integratedLUFSSum = 0;
    let validWindowsCount = 0;
    let peakAmplitude = 0;

    // Weighting filters (constants for ITU-R BS.1770)
    const preEmphasisB = [1, -1.9999695, 1]; // Pre-emphasis numerator (high-pass filter)
    const preEmphasisA = [1, -1.999939, 0.9999695]; // Pre-emphasis denominator

    const highShelfB = [1.5351249, -2.6916962, 1.1983924]; // High-shelf numerator
    const highShelfA = [1, -1.6906593, 0.7324808]; // High-shelf denominator

    // Weighted buffers
    const weightedBuffers = Array.from({ length: channels }, () => new Float32Array(bufferLength));

    // Helper: BiQuad filter application
    function applyBiQuad(input, b, a) {
        const output = new Float32Array(input.length);
        let x1 = 0, x2 = 0; // Input history
        let y1 = 0, y2 = 0; // Output history

        for (let i = 0; i < input.length; i++) {
            const x0 = input[i];
            const y0 = b[0] * x0 + b[1] * x1 + b[2] * x2 - a[1] * y1 - a[2] * y2;
            output[i] = y0;

            // Update history
            x2 = x1; x1 = x0;
            y2 = y1; y1 = y0;
        }

        return output;
    }

    // Apply ITU-R BS.1770 filters
    for (let channel = 0; channel < channels; channel++) {
        const data = decodedBuffer.getChannelData(channel);

        // Apply high-pass filter
        let filtered = applyBiQuad(data, preEmphasisB, preEmphasisA);

        // Apply high-shelf filter
        filtered = applyBiQuad(filtered, highShelfB, highShelfA);

        // Copy to weighted buffer
        weightedBuffers[channel] = filtered;

        // Update peak amplitude
        for (let i = 0; i < filtered.length; i++) {
            if (Math.abs(filtered[i]) > peakAmplitude) {
                peakAmplitude = Math.abs(filtered[i]);
            }
        }
    }

    // Helper to calculate LUFS for a window across channels
    function calculateLUFSForWindow(start, end) {
        let sumSquare = 0;
        const windowSize = end - start;

        for (let channel = 0; channel < channels; channel++) {
            const buffer = weightedBuffers[channel];
            for (let i = start; i < end; i++) {
                sumSquare += buffer[i] * buffer[i];
            }
        }

        if (windowSize === 0) return -Infinity;
        const meanSquare = sumSquare / (windowSize * channels);
        return 10 * Math.log10(meanSquare);
    }

    // Analyze overlapping windows
    for (let i = 0; i < bufferLength; i += Math.floor(sampleRate * 0.1)) {
        const momentaryEnd = Math.min(i + WINDOW_MOMENTARY, bufferLength);
        const shortTermEnd = Math.min(i + WINDOW_SHORT_TERM, bufferLength);

        // Momentary LUFS
        const momentaryLUFS = calculateLUFSForWindow(i, momentaryEnd);
        if (momentaryLUFS > momentaryMaxLUFS) {
            momentaryMaxLUFS = momentaryLUFS;
        }

        // Short-term LUFS
        const shortTermLUFS = calculateLUFSForWindow(i, shortTermEnd);
        if (shortTermLUFS > shortTermMaxLUFS) {
            shortTermMaxLUFS = shortTermLUFS;
        }

        // Integrated LUFS
        if (shortTermLUFS > THRESHOLD_LUFS) {
            integratedLUFSSum += Math.pow(10, shortTermLUFS / 10);
            validWindowsCount++;
        }
    }

    const integratedLUFS = validWindowsCount > 0
        ? 10 * Math.log10(integratedLUFSSum / validWindowsCount)
        : -Infinity;

    // Calculate Peak dB
    const peakDb = 20 * Math.log10(peakAmplitude);

    return {
        momentaryMaxLUFS,
        shortTermMaxLUFS,
        integratedLUFS,
        peakDb,
		peak: peakAmplitude,
    };
}

async function detectPitchFrequency(channelData, sampleRate, opts) {
	var result = await tuner.detectPitchFrequency(channelData, sampleRate || ctx.sampleRate, opts);
	if (__DEBUG__)
		console.debug('detectPitchFrequency', { channelData, sampleRate, opts, result });
	return result;
}

async function detectPitchFrequencyFromBlob(blob, opts) {
	const buffer = await blob2Buffer(blob);
	const channelData = buffer.getChannelData(0);
	return detectPitchFrequency(channelData, ctx.sampleRate, opts);
}

export default {
	StereoAnalyserNode,
	bufferToWave,
	detectBeat,
	getNextBeatPoint,
	getPeakValue,
	detectSilence,
	getAudioBuffer,
	dbToGain,
	gainToDb,
	gainToDbText,
	blob2Buffer,
	resampleBuffer,
	resampleBlob,
	calcExactLength,
	getBufferInfo,
	detectPitchFrequency,
	detectPitchFrequencyFromBlob,
	ctx
}

export {
	StereoAnalyserNode,
	bufferToWave,
	detectBeat,
	getNextBeatPoint,
	getPeakValue,
	detectSilence,
	getAudioBuffer,
	dbToGain,
	gainToDb,
	gainToDbText,
	blob2Buffer,
	resampleBuffer,
	resampleBlob,
	calcExactLength,
	getBufferInfo,
	detectPitchFrequency,
	detectPitchFrequencyFromBlob,
	ctx
}